import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, FileText, Trash, CalendarBlank, Buildings } from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: "bg-gray-500/10 text-gray-400 border-gray-500/20",
  finalized: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export default function ReportsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [businesses, setBusinesses] = useState([]);

  const [title, setTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [rResp, bResp] = await Promise.all([
          api.get("/reports"),
          api.get("/businesses"),
        ]);
        setReports(rResp.data.reports || []);
        setBusinesses(bResp.data.businesses || []);
      } catch (e) { console.error("Failed to load reports:", e); }
      setLoading(false);
    };
    fetch();
  }, [api]);

  const handleCreate = async () => {
    if (!title || !clientName || !startDate || !endDate || !businessId) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreating(true);
    try {
      const resp = await api.post("/reports", {
        title,
        client_name: clientName,
        business_id: businessId,
        report_period: { start_date: startDate, end_date: endDate },
      });
      setShowCreate(false);
      navigate(`/reports/${resp.data.report_id}`);
    } catch {
      toast.error("Failed to create report");
    }
    setCreating(false);
  };

  const handleDelete = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this report?")) return;
    try {
      await api.delete(`/reports/${reportId}`);
      setReports((prev) => prev.filter((r) => r.report_id !== reportId));
      toast.success("Report deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatPeriod = (period) => {
    if (!period?.start_date) return "";
    try {
      const s = new Date(period.start_date);
      const e = new Date(period.end_date);
      return `${s.toLocaleDateString("en-US", { month: "short", year: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
    } catch (e) { console.error("Date parse failed:", e); return ""; }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="reports-title">Reports</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Package content into professional client deliverables</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="new-report-btn">
            <Plus size={16} className="mr-1.5" /> New Report
          </Button>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={`skeleton-${i}`} className="h-40 bg-[var(--bg-card)] rounded-lg animate-pulse border border-[var(--border)]" />)}
          </div>
        ) : reports.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20" data-testid="reports-empty">
            <FileText size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-4" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No reports yet</h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md mx-auto">
              Create a report to package your generated content into a professional PDF deliverable for clients.
            </p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white">
              <Plus size={16} className="mr-1.5" /> Create Your First Report
            </Button>
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
            {reports.map((r, i) => (
              <motion.div
                key={r.report_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/reports/${r.report_id}`)}
                className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 cursor-pointer hover:border-[#FF4400]/30 hover:shadow-md transition-all group"
                data-testid={`report-card-${r.report_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded border uppercase ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                    {r.status}
                  </span>
                  <button
                    onClick={(e) => handleDelete(e, r.report_id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-[var(--text-muted)] hover:text-red-400 hover:bg-red-400/10 transition-all"
                    data-testid={`delete-report-${r.report_id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1 truncate">{r.title}</h3>
                <p className="text-xs text-[var(--text-muted)] mb-3">{r.client_name}</p>
                <div className="flex items-center gap-3 text-[10px] text-[var(--text-muted)]">
                  <span className="flex items-center gap-1"><CalendarBlank size={12} /> {formatPeriod(r.report_period)}</span>
                  <span className="flex items-center gap-1"><FileText size={12} /> {(r.sections || []).filter((s) => s.section_type === "content_item").length} items</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Report Modal */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-md" data-testid="create-report-modal">
            <DialogHeader>
              <DialogTitle className="text-[var(--text-primary)]">New Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Report Title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="April 2025 Content Report" className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="report-title-input" />
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Client Name</label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Acme Corp" className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="report-client-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Start Date</label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="report-start-date" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">End Date</label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="report-end-date" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">Assign to Business</label>
                <Select value={businessId} onValueChange={setBusinessId}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)]" data-testid="report-business-select">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((b) => (
                      <SelectItem key={b.business_id} value={b.business_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-[var(--text-secondary)]">Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="create-report-submit">
                {creating ? "Creating..." : "Create Report"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
